<template>
  <div>
    <a-modal
      class="page-modal"
      v-model:visible="modalVisible"
      title="Identifiers"
      :style="{ width: '800px' }"
      destroyOnClose
    >
      <AgGridVue
        style="width: 100%; height: -webkit-calc(100vh - 500px)"
        :theme="themeBalham"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowData="localIdentifiers"
      />
      <template #footer>
        <a-button key="add" type="primary" @click="showAddModal">Add</a-button>
        <a-button key="cancel" @click="handleCancel">Cancel</a-button>
        <a-button
          key="save"
          type="primary"
          @click="handleSave"
          :loading="loading"
          >Save</a-button
        >
      </template>
    </a-modal>

    <a-modal
      class="page-modal"
      v-model:visible="editModalVisible"
      :title="isEdit ? 'Edit Identifier' : 'Add Identifier'"
      :style="{ width: '500px' }"
      @ok="handleOk"
      @cancel="handleEditCancel"
      destroyOnClose
    >
      <a-form :model="editForm" layout="vertical">
        <a-form-item label="Type">
          <a-select
            v-model:value="editForm.type"
            placeholder="All"
            :disabled="isEdit"
            :show-search="true"
            :filter-option="filterOption"
          >
            <a-select-option
              v-for="item in identifierTypeList"
              :key="item"
              :value="item"
            >
              {{ item }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="Value">
          <a-input v-model:value="editForm.value" />
        </a-form-item>
        <a-form-item label="Effective From">
          <a-date-picker
            v-model:value="editForm.effectiveFromDate"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item label="Effective To">
          <a-date-picker
            v-model:value="editForm.effectiveToDate"
            style="width: 100%"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup>
import {
  ref,
  defineExpose,
  computed,
  defineProps,
  defineEmits,
  inject,
  watchEffect,
} from 'vue'
import { themeBalham } from 'ag-grid-community'
import AgEditCellRenderer from '@/components/AgEditCellRenderer.vue'
import { message } from 'ant-design-vue'
import dayjs from 'dayjs'

defineExpose({
  AgEditCellRenderer,
})

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  rawData: {
    type: Object,
    default: () => ({
      securityId: null,
      identifiers: [],
    }),
  },
})

const emit = defineEmits(['update:visible', 'update:identifiers'])

const identifierTypeList = inject('identifierTypeList', ref([]))
const security = inject('security')

const modalVisible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val),
})
const securityId = ref(null)
const localIdentifiers = ref([])
// Modal state for add/edit
const isEdit = ref(false)
const editModalVisible = ref(false)
const editForm = ref({
  type: '',
  value: '',
  effectiveFromDate: null,
  effectiveToDate: null,
})
const changed = ref(false)
const loading = ref(false)

watchEffect(() => {
  if (props.visible) {
    securityId.value = props.rawData.securityId
    Object.assign(localIdentifiers.value, { ...props.rawData.identifiers })
    changed.value = false
  } else {
    securityId.value = null
    localIdentifiers.value = []
  }
})

const defaultColDef = {
  filter: true,
  sortable: true,
  resizable: true,
}

let columnDefs = [
  { field: 'type', headerName: 'Type', flex: 1, minWidth: 120 },
  { field: 'value', headerName: 'Value', flex: 2, minWidth: 180 },
  {
    field: 'effectiveFromDate',
    headerName: 'Effective From',
    flex: 1,
    minWidth: 140,
  },
  {
    field: 'effectiveToDate',
    headerName: 'Effective To',
    flex: 1,
    minWidth: 140,
  },
  {
    headerName: 'Action',

    field: 'action',
    width: 120,
    filter: false,
    cellRenderer: 'AgEditCellRenderer',
    cellRendererParams: {
      editButton: 'Edit',
      showEditModal: (params) => showEditModal(params.data),
      deleteButton: 'Delete',
      onDelete: (params) => deleteIdentifier(params.data),
      deleteDisabled: (params) => isLastBbTcm(params.data),
      deleteDisabledTitle:
        'At least one BB_TCM or CFD_BB_TCM identifier is required',
    },
  },
]

// Show add modal
const showAddModal = () => {
  isEdit.value = false
  editForm.value = {
    type: '',
    value: '',
    effectiveFromDate: null,
    effectiveToDate: null,
  }
  editModalVisible.value = true
}

// Show edit modal
const showEditModal = (event) => {
  isEdit.value = true
  const data = event?.data || event
  editForm.value = {
    ...data,
    effectiveFromDate: data.effectiveFromDate
      ? dayjs(data.effectiveFromDate)
      : null,
    effectiveToDate: data.effectiveToDate ? dayjs(data.effectiveToDate) : null,
  }

  editModalVisible.value = true
}

// Types that must always keep at least one entry
const BB_TCM_TYPES = ['BB_TCM', 'CFD_BB_TCM']

const isBbTcmType = (type) =>
  BB_TCM_TYPES.includes(
    String(type ?? '')
      .replace(/^_+/, '')
      .toUpperCase()
  )

// True when the row is the only remaining BB_TCM / CFD_BB_TCM identifier
const isLastBbTcm = (data) =>
  isBbTcmType(data?.type) &&
  localIdentifiers.value.filter((identifier) => isBbTcmType(identifier.type))
    .length <= 1

const deleteIdentifier = (data) => {
  if (isLastBbTcm(data)) {
    return
  }

  const index = localIdentifiers.value.findIndex(
    (identifier) => identifier.type === data.type
  )

  if (index !== -1) {
    localIdentifiers.value.splice(index, 1)
  }
  changed.value = true
}

const filterOption = (input, option) => {
  return option.key.toLowerCase().indexOf(input.toLowerCase()) >= 0
}

// Save add/edit
const handleOk = () => {
  const formatDateTime = (val) =>
    val ? dayjs(val).format('YYYY-MM-DDTHH:mm:ss') : null
  if (!isEdit.value) {
    const identifier = {
      type: editForm.value.type,
      value: editForm.value.value,
      effectiveFromDate: formatDateTime(editForm.value.effectiveFromDate),
      effectiveToDate: formatDateTime(editForm.value.effectiveToDate),
    }
    localIdentifiers.value.push(identifier)
  } else {
    const index = localIdentifiers.value.findIndex(
      (identifier) => identifier.type === editForm.value.type
    )
    if (index !== -1) {
      localIdentifiers.value[index] = {
        type: editForm.value.type,
        value: editForm.value.value,
        effectiveFromDate: formatDateTime(editForm.value.effectiveFromDate),
        effectiveToDate: formatDateTime(editForm.value.effectiveToDate),
      }
    }
  }
  changed.value = true
  editModalVisible.value = false
}

// Cancel edit/add
const handleEditCancel = () => {
  editModalVisible.value = false
}

const handleSave = () => {
  if (changed.value) {
    loading.value = true
    // emit('update:identifiers', securityId.value, localIdentifiers.value)
    security
      .updateIdentifiers(securityId.value, localIdentifiers.value)
      .then(() => {
        message.success('Identifiers updated successfully')
        modalVisible.value = false
      })
      .catch((error) => {
        console.error('Error updating identifiers:', error)
        message.error(error.response.data.message)
      })
      .finally(() => {
        loading.value = false
      })
  }
}

const handleCancel = () => {
  modalVisible.value = false
}
</script>
